import { ActivatedRoute, Params, Router } from '@angular/router';
import { AfterViewChecked, Component, OnDestroy, OnInit } from '@angular/core';
import { AnnouncementService, CertificationsTrainingService } from '@app/services/_index';
import { AnnouncementSlugs, CardTypeCallToActions, RouteTypes } from '@app/enums/_index';
import { HasPageTitleAndMetaData, StaticPageInterface } from '@app/interfaces/_index';
import { Meta, Title } from '@angular/platform-browser';

import { StaticPage } from '@app/models/_index';
import { StaticPageAdapter } from '@app/adapters/_index';
import { Subscription } from 'rxjs';
import { scrollIntoView } from '@app/utilities/_index';

@Component({
  selector: 'app-certifications-training',
  templateUrl: './certifications-training.component.html',
  styleUrls: ['./certifications-training.component.scss'],
  standalone: false,
})
export class CertificationsTrainingComponent
  implements OnInit, OnDestroy, HasPageTitleAndMetaData, AfterViewChecked
{
  // Page Properties
  certificationContent: StaticPage;
  parentNavItems: StaticPage[] = [];
  childNavItems: StaticPage[] = [];

  certificationAnnouncementContent: StaticPage;
  certificationBadgeAnnouncementContent: StaticPage;
  subs: Subscription[] = [];
  isLoading: boolean;
  fragment: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private announcementService: AnnouncementService,
    private certificationContentService: CertificationsTrainingService,
    private titleService: Title,
    private metaService: Meta,
    private staticPageAdapter: StaticPageAdapter
  ) {}

  /**
   * On Init, call getCertification to load data
   */
  ngOnInit(): void {
    this.isLoading = true;
    // Calling the Title and Meta tag.
    this.setTitleAndMetaData();
    this.subs.push(
      this.route.params.subscribe((params: Params) => {
        // Pull Certification Content based on Slug selected.
        this.getCertificationContentBySlug(params);
      })
    );
    // Call the Certification Content.
    this.getCertificationContent();
    this.isLoading = false;
  }

  /**
   * Routes the page based on the parameters passed
   */
  getCertificationContent(): void {
    // Pull all the Certification content.
    this.getAllCertification();
    // Get the Certification Announcement content.
    this.getCertificationAnnouncementContent();
    this.getCertificationBageAnnouncementContent();
  }

  /**
   * Pull all the Certification Training contents
   */
  getAllCertification(): void {
    this.subs.push(
      this.certificationContentService.getCertificationContent().subscribe({
        next: (response: StaticPageInterface[]) => {
          if (response && response.length > 0) {
            // TODO: This is repetitive in other pages, need to refactor
            const staticPages: StaticPage[] = response.map((item: StaticPageInterface) => {
              return this.staticPageAdapter.adapt(item);
            });
            // Filter out the parent quick start guide items that have no children if parent id is 0
            this.parentNavItems = staticPages.filter((staticPage) => staticPage.parent === 0);
            this.childNavItems = staticPages.filter((staticPage) => staticPage.parent !== 0);
            this.isLoading = false;
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Certifications Training content completed');
        },
      })
    );
  }

  /**
   * Get the Certification Announcement content.
   */
  getCertificationAnnouncementContent(): void {
    const wordpressSlugName = AnnouncementSlugs.CERTIFICATION_SLUG;
    this.subs.push(
      this.announcementService.getAnnouncementContentBySlug(wordpressSlugName).subscribe({
        next: (response: StaticPageInterface[]) => {
          if (response && response.length > 0) {
            this.certificationAnnouncementContent = this.staticPageAdapter.adapt(response[0]);
            this.isLoading = false;
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Certifications Training content completed');
        },
      })
    );
  }

  /**
   *  Get the Certification Badge Announcement content.
   */
  getCertificationBageAnnouncementContent(): void {
    const wordpressSlugName = AnnouncementSlugs.CERTIFICATION_BADGE_SLUG;
    this.subs.push(
      this.announcementService.getAnnouncementContentBySlug(wordpressSlugName).subscribe({
        next: (response: StaticPageInterface[]) => {
          if (response && response.length > 0) {
            this.certificationBadgeAnnouncementContent = this.staticPageAdapter.adapt(response[0]);
            this.isLoading = false;
            console.log(response);
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Certifications Training content completed');
        },
      })
    );
  }

  /**
   * Get Certification Content by slug
   * @param params indicates value for each slug selected
   */
  getCertificationContentBySlug(params: any): void {
    this.subs.push(
      this.certificationContentService.getCertificationContent().subscribe({
        next: (response: StaticPageInterface[]) => {
          if (response && response.length > 0) {
            // Redirect to 404 page if the slug does not matches.
            if (!response.some((x) => x.slug === params.slug)) {
              this.isLoading = false;
              this.router.navigateByUrl('pagenotfound');
            }
            response.filter((item: StaticPageInterface) => {
              if (item.slug === params.slug) {
                this.certificationContent = this.staticPageAdapter.adapt(item);
              }
            });
            this.isLoading = false;
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Certifications Training content completed');
        },
      })
    );
    // The fragment allows the link # page jump to jump to specific content on page
    this.subs.push(
      this.route.fragment.subscribe((fragment: string) => {
        this.fragment = fragment;
      })
    );
  }

  /**
   * Set selected Certification title and metadata tags
   */
  setTitleAndMetaData(): void {
    // NOTE: Update page title on certification page
    this.titleService.setTitle('Certifications | Genesys Beyond');
    // NOTE: Update meta tag description for page
    this.metaService.updateTag({
      name: 'description',
      content:
        'See all of your Genesys Beyond enrollments, certifications, and recommended learning in one place.',
    });
  }

  /**
   *  CardTypeCallToActions enums based on card types
   */
  public get cardTypeCallToActions(): typeof CardTypeCallToActions {
    return CardTypeCallToActions;
  }

  /**
   *  RouteTypes enums based on route types
   */
  public get routeTypes(): typeof RouteTypes {
    return RouteTypes;
  }

  /**
   * On Destroy unsubscribe the services to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  /**
   * Scroll to View using fragment or link a href # alias
   */
  ngAfterViewChecked(): void {
    if (this.fragment) {
      scrollIntoView(this.fragment, 'start');
    }
  }

  /**
   * Add Track By Announcement Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - index of Static Page Array
   * @param item -  Static Page item
   * @returns announcement id
   */
  trackByAnnouncementId(index: number, item: StaticPage): number {
    if (item) {
      return item.id;
    } else {
      return index;
    }
  }
}
